/**
 * RefreshForecast page — yearly replacement projections and budget estimates.
 *
 * Shows a table of assets reaching end-of-life per year with estimated
 * replacement costs, broken down by device type.
 *
 * Requirements: 39.14, 39.15, 39.16
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { assetService } from '@/services/assetService';

function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function RefreshForecast() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['assets', 'refresh-forecast'],
    queryFn: () => assetService.getRefreshForecast(),
  });

  if (isLoading) return <p>Loading refresh forecast...</p>;
  if (error) return <p style={{ color: 'red' }}>Error: {(error as Error).message}</p>;
  if (!data) return <p>No forecast data available.</p>;

  const totalDevices = data.forecast.reduce((sum, y) => sum + y.totalCount, 0);
  const totalCost = data.forecast.reduce((sum, y) => sum + y.estimatedCost, 0);

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/assets">← Back to Assets</Link>
      </div>

      <h1>Device Refresh Forecast</h1>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', minWidth: '200px' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Devices to Refresh</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalDevices}</div>
        </div>
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', minWidth: '200px' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Estimated Cost</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalCost)}</div>
        </div>
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', minWidth: '200px' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Refresh Thresholds</div>
          <div style={{ fontSize: '0.9rem' }}>
            Laptop: {data.thresholds.laptop} yr | Desktop: {data.thresholds.desktop} yr
          </div>
        </div>
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', minWidth: '200px' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Avg. Replacement Cost</div>
          <div style={{ fontSize: '0.9rem' }}>
            Laptop: {formatCurrency(data.averageCosts.laptop)} | Desktop: {formatCurrency(data.averageCosts.desktop)}
          </div>
        </div>
      </div>

      {/* Forecast Table */}
      <h2>Yearly Replacement Projections</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Year</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Laptops</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Desktops</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Workstations</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Total Devices</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '2px solid #ddd' }}>Estimated Cost</th>
          </tr>
        </thead>
        <tbody>
          {data.forecast.map((year) => (
            <tr key={year.year} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>{year.year}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{year.laptopCount}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{year.desktopCount}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{year.workstationCount}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{year.totalCount}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                {formatCurrency(year.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #333' }}>
            <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>Total</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
              {data.forecast.reduce((s, y) => s + y.laptopCount, 0)}
            </td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
              {data.forecast.reduce((s, y) => s + y.desktopCount, 0)}
            </td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
              {data.forecast.reduce((s, y) => s + y.workstationCount, 0)}
            </td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{totalDevices}</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(totalCost)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Visual Bar Chart */}
      <h2 style={{ marginTop: '2rem' }}>Cost by Year</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {data.forecast.map((year) => {
          const maxCost = Math.max(...data.forecast.map((y) => y.estimatedCost), 1);
          const widthPct = (year.estimatedCost / maxCost) * 100;
          return (
            <div key={year.year} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: '50px', fontWeight: 'bold' }}>{year.year}</span>
              <div style={{
                height: '24px',
                width: `${Math.max(widthPct, 1)}%`,
                maxWidth: '80%',
                backgroundColor: '#1a73e8',
                borderRadius: '4px',
                transition: 'width 0.3s',
              }} />
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                {year.totalCount} devices — {formatCurrency(year.estimatedCost)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
